import { Box, FormField, Heading, TextButton } from '@wix/design-system'
import { Search } from '@wix/wix-ui-icons-common'
import React, { FC, useMemo } from 'react'

import { ambassadorHttpClient } from '../../../api/httpClient'
import { BusinessTermService } from '../../reusable/Category/api/BusinessTermService'
import { AutoCompleteWithOverrides } from '../../reusable/Category/AutoCompleteWithOverrides'
import { useCategory } from '../../reusable/Category/useCategory'
import { FancyAutoComplete } from '../../reusable/FancyAutoComplete'
import ArrowUpLeft from '../../reusable/icons/ArrowUpLeft.svg'
import ArrowUpRight from '../../reusable/icons/ArrowUpRight.svg'
import WixStudioLogo from '../../reusable/icons/WixStudioLogo.svg'
import s from './index.scss'

export const WixStudioCategory: FC = () => {
  const businessTermService = useMemo(() => new BusinessTermService(ambassadorHttpClient), [])

  const {
    handlers: {
      handleClear,
      handleInputClick,
      handleSearchSelect,
      handleNextClick,
      handleSearchChange,
      handleSkipClick,
      handleBackClick,
      handleSuggestionClick,
    },
    state: { search = '', category, categoriesLoading },
    categoriesOptions,
    content: {
      isAddUserInputAsOption,
      placeholder,
      title,
      suggestionsLabel,
      suggestions,
      nextButton,
      skipButton,
      backButton,
    },
    autoCompleteRef,
    selectedId,
  } = useCategory({
    businessTermService,
  })

  const prefixNode = useMemo(
    () => (
      <Box height="100%" verticalAlign="middle">
        <Search width={20} height={20} />
      </Box>
    ),
    [],
  )

  const isSearchValue = !!search.trim()

  return (
    <div className={s.layout}>
      <div className={s.header}>
        <WixStudioLogo height={21} width={117} />
      </div>
      <div className={s.content}>
        <Heading as="h1" className={s.title}>
          {title}
        </Heading>
        <div className={s.modal}>
          <div className={s.formField}>
            <FormField>
              <FancyAutoComplete
                prefixNode={prefixNode}
                popoverProps={{ moveBy: { y: 10 } }}
                ref={autoCompleteRef}
                value={search}
                selectedId={selectedId}
                onChange={handleSearchChange}
                status={categoriesLoading ? 'loading' : undefined}
                options={categoriesOptions}
                placeholder={placeholder}
                size="large"
                onSelect={handleSearchSelect}
                onInputClicked={handleInputClick}
                className={s.searchField}
                border="bottomLine"
                onClear={categoriesLoading ? undefined : handleClear}
                customRenderer={AutoCompleteWithOverrides}
                isIconHidden={isAddUserInputAsOption}
              />
            </FormField>
            <button className={s.nextButton} onClick={handleNextClick} disabled={!category}>
              {nextButton}
              {isSearchValue && category ? <ArrowUpRight className={s.rightIcon} /> : null}
            </button>
          </div>
          <div className={s.listContainer}>
            {suggestionsLabel}
            <ul className={s.list}>
              {suggestions.items.map(({ text }, i) => (
                <li key={i}>
                  <button className={s.listItem} onClick={() => handleSuggestionClick(text, i)}>
                    {text}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className={s.footer}>
        <TextButton
          className={s.navigationButton}
          size="small"
          prefixIcon={<ArrowUpLeft className={s.footerLeftIcon} />}
          skin="dark"
          onClick={handleBackClick}
        >
          {backButton}
        </TextButton>
        <TextButton
          className={s.navigationButton}
          size="small"
          suffixIcon={<ArrowUpRight className={s.footerRightIcon} />}
          skin="dark"
          onClick={handleSkipClick}
        >
          {skipButton}
        </TextButton>
      </div>
    </div>
  )
}
